import OrganizationSwitchNotPerformedError from "./OrganizationSwitchNotPerformedError";

// LSP-19027: `setOrganizationId` had no serialization at all, so two overlapping switches to
// DIFFERENT organizations both ran a refresh-token grant and whichever resolved last won by
// accident. The UI could settle on organization B while the emitted access token — and the Auth0
// SDK's own token cache, which every later `getAccessToken()` reads through — still carried
// organization A. That is the same customer-facing wrong-organization failure LSP-18866 closed
// from the failed-switch side, reached through the concurrent-switch door instead.
//
// A superseded switch is NOT a failure, and the two must not be collapsed into one fact (the
// one-flag-two-facts lesson recorded throughout auth.js): "a newer switch owns the organization
// now" and "the switch was attempted and broke" call for different diagnostics even though they
// call for the SAME caller response. So this carries its own marker for classification and
// logging, while deliberately INHERITING `organizationSwitchNotPerformed` from its base so every
// existing consumer keeps behaving correctly without being touched:
//
//   - the router's authenticated-boot reconcile already branches on
//     isOrganizationSwitchNotPerformedError and defers + replays the route rather than continuing
//     boot. That is exactly right for a supersession: continuing would complete boot against
//     `me.organization.id` while the token is becoming a different organization's — the very
//     defect. The replay re-runs getMe on the WINNER's token, so its target converges to the
//     winning organization and the retry resolves "already-current" instead of racing again.
//   - useOrgSwitch and the cross-org notification click already catch and fail safe (return false
//     / do not navigate), which is the correct response to "your click was superseded by a later
//     click".
//
// Never thrown for a switch that was attempted and failed — that keeps its own error verbatim, so
// a genuine outage can never be laundered into a benign-looking supersession.
export default class OrganizationSwitchSupersededError extends OrganizationSwitchNotPerformedError {
	// readonly for the same reason as the base marker: consumers branch on it.
	readonly organizationSwitchSuperseded: boolean;
	// The organization that WON, when it is known. Null when the incumbent released ownership
	// between the losing check and the throw — "I was superseded but I can no longer say by whom"
	// is a real answer and is not dressed up as knowledge we do not have.
	readonly supersededByOrganizationId: string | null;
	readonly wasExplicitUserIntent: boolean;

	constructor(organizationId: string | null, supersededByOrganizationId: string | null, wasExplicitUserIntent: boolean) {
		super(`superseded by a newer organization switch to ${supersededByOrganizationId ?? "unknown"}`, organizationId);
		this.name = "OrganizationSwitchSupersededError";
		this.organizationSwitchSuperseded = true;
		this.supersededByOrganizationId = supersededByOrganizationId ?? null;
		this.wasExplicitUserIntent = wasExplicitUserIntent;
	}
}

// Duck-typed on the marker alone, for the same reasons as the base helper: the error crosses an
// `await` and a module boundary, so `instanceof` breaks under a duplicated module instance.
export function isOrganizationSwitchSupersededError(error: unknown): error is OrganizationSwitchSupersededError {
	return (error as { organizationSwitchSuperseded?: unknown } | null | undefined)?.organizationSwitchSuperseded === true;
}
